/**
 * Pure ordering helpers for DraggableCardGrid, kept out of the component so the
 * band-aware persistence logic is unit-testable without a DOM.
 *
 * A card belongs to a `section` (band). Cards only ever reorder WITHIN their
 * band; the effective render order is, per band in first-appearance order, the
 * saved order (present cards of that band only), with each card the saved order
 * has never seen placed right after the card it follows by default.
 * The flattened result is contiguous per band (band 0 keys, then band 1, ...),
 * which is what lets the drag handler move a card within its band by splicing
 * against its same-band neighbours in the flat array.
 */

export type CardMeta = {
  key: string
  section: number
}

export type BandedOrder = {
  /** Flat key order across all bands, contiguous per band, in render order. */
  flat: string[]
  /** Per-band ordered key lists, keyed by section index. */
  bands: Map<number, string[]>
}

export function computeBandedOrder(cards: CardMeta[], saved: string[]): BandedOrder {
  const sectionsInOrder: number[] = []
  cards.forEach((c) => {
    if (!sectionsInOrder.includes(c.section)) sectionsInOrder.push(c.section)
  })
  const bands = new Map<number, string[]>()
  const flat: string[] = []
  sectionsInOrder.forEach((s) => {
    const bandKeys = cards.filter((c) => c.section === s).map((c) => c.key)
    const bandSet = new Set(bandKeys)
    const ordered = saved.filter((k) => bandSet.has(k))
    bandKeys.forEach((key, i) => {
      if (ordered.includes(key)) return
      // A new card keeps its default place: after its natural predecessor, or
      // first when nothing precedes it.
      const before = bandKeys
        .slice(0, i)
        .reverse()
        .find((k) => ordered.includes(k))
      ordered.splice(before ? ordered.indexOf(before) + 1 : 0, 0, key)
    })
    bands.set(s, ordered)
    flat.push(...ordered)
  })
  return { flat, bands }
}

/**
 * SUPAGARD-26: fold a drag's result back into the full saved order.
 *
 * A drag only ever sees the cards on screen, but the saved order is shared by
 * every record of the page, and records show different cards (a registration
 * with no dealer has no Dealer card; a hidden card is off the grid). Saving the
 * drag's list as-is dropped every card this record did not show, so the next
 * record that DID show it fell back to its default place, and the user's
 * arrangement looked like it had not stuck.
 *
 * `next` (the on-screen order) wins for the cards it holds. Every card only the
 * previous saved order knows is kept, right after the card it followed there.
 */
export function mergeSavedOrder(previous: string[] | null, next: string[]): string[] {
  const merged = next.slice()
  if (!previous) return merged
  const present = new Set(next)
  // Walking the saved order, the card just handled is always the nearest one
  // before the current card that `merged` already holds, so an unseen card goes
  // straight after it (or first, when nothing precedes it).
  let insertAt = 0
  previous.forEach((key) => {
    if (present.has(key)) {
      insertAt = merged.indexOf(key) + 1
    } else {
      merged.splice(insertAt, 0, key)
      insertAt += 1
    }
  })
  return merged
}
